/*
  Premium navigation enhancer for the Amadeus theme.

  Two responsibilities, both non-blocking and fully degradable:
    1. Prefetch internal pages on user intent (hover / touch / focus) so
       navigation feels instant. Skipped on Save-Data / slow connections.
    2. Reveal the branded overlay ONLY when a real navigation is slow
       (> THRESHOLD_MS), never on fast loads, and never let it stay stuck.

  Overlay markup lives in snippets/page-transition-overlay.liquid.
*/
(function () {
  'use strict';

  var THRESHOLD_MS = 250; // reveal the overlay only for navigations slower than this
  var SAFETY_TIMEOUT_MS = 8000; // hard ceiling — the overlay can never stay stuck

  var overlay = null;
  var revealTimer = null;
  var safetyTimer = null;
  var prefetched = Object.create(null);

  function getOverlay() {
    if (!overlay) {
      overlay = document.getElementById('PageTransition');
    }
    return overlay;
  }

  // ── Branded overlay (slow-navigation fallback) ────────────────────────────

  function showOverlay() {
    var el = getOverlay();
    if (el) {
      el.classList.add('is-active');
    }
  }

  function hideOverlay() {
    var el = getOverlay();
    if (el) {
      el.classList.remove('is-active');
    }
  }

  function clearTimers() {
    if (revealTimer) {
      window.clearTimeout(revealTimer);
      revealTimer = null;
    }
    if (safetyTimer) {
      window.clearTimeout(safetyTimer);
      safetyTimer = null;
    }
  }

  function scheduleOverlay() {
    clearTimers();
    revealTimer = window.setTimeout(showOverlay, THRESHOLD_MS);
    safetyTimer = window.setTimeout(hideOverlay, SAFETY_TIMEOUT_MS);
  }

  // ── Eligibility ───────────────────────────────────────────────────────────
  // A link must be a same-origin, plain left-click navigation to a different
  // page. Everything else (new tab, download, hash, mail/tel, JS-handled,
  // external) is left untouched.

  function isEligible(link, event) {
    if (!link || !link.href) {
      return false;
    }
    if (event) {
      // Bubble-phase click: defaultPrevented is true if drawer/cart/etc. JS
      // already claimed this link — in that case there is no full navigation.
      if (event.defaultPrevented) {
        return false;
      }
      if (event.button && event.button !== 0) {
        return false;
      }
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return false;
      }
    }
    if (link.target && link.target !== '_self') {
      return false;
    }
    if (link.hasAttribute('download')) {
      return false;
    }
    if (link.origin !== window.location.origin) {
      return false;
    }

    var rel = (link.rel || '').toLowerCase();
    if (rel.indexOf('external') !== -1 || rel.indexOf('nofollow') !== -1) {
      return false;
    }

    var raw = link.getAttribute('href') || '';
    if (/^(mailto:|tel:|#)/i.test(raw)) {
      return false;
    }

    // Same page, only the hash differs → no navigation.
    if (
      link.pathname === window.location.pathname &&
      link.search === window.location.search &&
      link.hash
    ) {
      return false;
    }

    return true;
  }

  // ── Prefetch on intent ────────────────────────────────────────────────────

  function connectionAllowsPrefetch() {
    var c = navigator.connection;
    if (!c) {
      return true;
    }
    if (c.saveData) {
      return false;
    }
    return !/(^|-)2g$/.test(c.effectiveType || '');
  }

  function prefetch(url) {
    if (prefetched[url] || !connectionAllowsPrefetch()) {
      return;
    }
    prefetched[url] = true;
    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.appendChild(link);
  }

  function onIntent(event) {
    var link = event.target.closest && event.target.closest('a[href]');
    if (link && isEligible(link, null)) {
      prefetch(link.href);
    }
  }

  // ── Navigation click ──────────────────────────────────────────────────────

  function onClick(event) {
    var link = event.target.closest && event.target.closest('a[href]');
    if (link && isEligible(link, event)) {
      scheduleOverlay();
    }
  }

  // ── Lifecycle / bfcache ───────────────────────────────────────────────────
  // back/forward can restore this page from bfcache with the overlay still
  // visible — always reset on show.

  window.addEventListener('pageshow', function () {
    clearTimers();
    hideOverlay();
  });

  document.addEventListener('mouseover', onIntent, { passive: true });
  document.addEventListener('touchstart', onIntent, { passive: true });
  document.addEventListener('focusin', onIntent, { passive: true });
  document.addEventListener('click', onClick);
})();
